import { useState, useCallback, useEffect,useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { authAPI } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, Sun, Moon, ShieldCheck, Hash, Play } from 'lucide-react';
import { MetaTradifyLogo } from '@/components/MetaTradifyLogo';
import { toast } from 'sonner';
import { useGoogleLogin } from '@react-oauth/google';
export default function Demo() {
  const turnstileRef = useRef(null);
  const { login, setAuth } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [show2fa, setShow2fa] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [tfaCode, setTfaCode] = useState('');
  const [loginMode, setLoginMode] = useState('email');
  const [googleLoading, setGoogleLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);

const [captchaToken, setCaptchaToken] = useState(null);

  // Handle Google OAuth access_token from hash fragment (implicit flow)
//   const handleGoogleToken = useCallback(async (accessToken) => {
//   setGoogleLoading(true);
//   try {
//     const { data } = await authAPI.googleLogin(accessToken);
//     // EXISTING USER LOGIN
//     localStorage.setItem('token', data.token);
//     setAuth(data.user, data.token);
//     toast.success(`Welcome, ${data.user.name}!`);
//     navigate(data.user.role === 'admin' ? '/admin' : '/dashboard');

//   } catch (err) {
//     toast.error(err.response?.data?.detail || 'Google login failed');
//   } finally {
//     setGoogleLoading(false);
//   }
  // }, [navigate, setAuth]);
useEffect(() => {
  if (!window.turnstile || !turnstileRef.current) return;

  // 🔥 prevent double render
  if (turnstileRef.current.hasChildNodes()) return;

  window.turnstile.render(turnstileRef.current, {
    sitekey: "0x4AAAAAAC2ixNnM6mujfOy5",
    callback: (token) => setCaptchaToken(token),
    theme: "light",
    size: "flexible"
  });

}, []);;
const googleLogin = useGoogleLogin({
  onSuccess: async (tokenResponse) => {

    setGoogleLoading(true);

    try {

      const { data } = await authAPI.googleLogin({
        access_token: tokenResponse.access_token
      });

      // ✅ NEW USER
      if (data.register_required) {

        navigate("/register", {
          state: {
            googleData: data.google_data
          }
        });

        return;
      }

      // ✅ EXISTING USER
      localStorage.setItem("token", data.token);
if (
  window.location.pathname.startsWith(
    '/mobile-auth'
  )
) {

  window.location.href =
    `metatradify://auth?token=${data.token}`;

  return;
}
      setAuth(data.user, data.token);

      toast.success(`Welcome, ${data.user.name}!`);

      navigate(data.user.role === "admin" ? "/admin" : "/dashboard");

    } catch (err) {
      toast.error(err.response?.data?.detail || "Google login failed");
    } finally {
      setGoogleLoading(false);
    }

  },

  onError: () => {
    toast.error("Google login failed");
  }
});
  // Handle legacy session_id callback (backward compat)
  const handleGoogleCallback = useCallback(async (sessionId) => {
    setGoogleLoading(true);
    try {
      const { data } = await authAPI.googleCallback({ session_id: sessionId });
      localStorage.setItem('token', data.token);
      setAuth(data.user, data.token);
      toast.success(`Welcome, ${data.user.name}!`);
      navigate(data.user.role === 'admin' ? '/admin' : '/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Google login failed');
    } finally { setGoogleLoading(false); }
  }, [navigate, setAuth]);

  // useEffect(() => {
  //   // Check for Google OAuth implicit flow token in URL hash
  //   const hash = window.location.hash;
  //   if (hash) {
  //     const params = new URLSearchParams(hash.substring(1));
  //     const accessToken = params.get('access_token');
  //     if (accessToken) {
  //       window.history.replaceState(null, '', window.location.pathname);
  //       handleGoogleToken(accessToken);
  //       return;
  //     }
  //   }
  //   // Legacy session_id flow
  //   const sid = searchParams.get('session_id');
  //   if (sid) handleGoogleCallback(sid);
  //   // Show session-expired message
  //   const sessionMsg = localStorage.getItem('session_expired');
  //   if (sessionMsg) {
  //     toast.error(sessionMsg);
  //     localStorage.removeItem('session_expired');
  //   }
  // }, [searchParams, handleGoogleCallback, handleGoogleToken]);

  const handleSubmit = async (e) => {
  e.preventDefault();

  if (!form.user_id || !form.password) {
    toast.error('Please fill in all fields');
    return;
  }

  // 🔐 CAPTCHA CHECK
  if (!captchaToken) {
    toast.error('Please verify captcha');
    return;
  }

  setLoading(true);

  try {
    const data = await login(
      form.user_id,
      form.password,
      captchaToken   
    );

    if (data.requires_2fa) {
      setShow2fa(true);
      setTempToken(data.temp_token);
      toast.info('Enter your 2FA code');
      setLoading(false);
      return;
    }

    toast.success('Welcome back!');
    navigate(data.user?.role === 'admin' ? '/admin' : '/dashboard');

  } catch (err) {
    toast.error(err.response?.data?.detail || 'Login failed');
  } finally {
    setLoading(false);
  }
};

  const handle2faSubmit = async (e) => {
    e.preventDefault();
    if (!tfaCode || tfaCode.length !== 6) { toast.error('Enter a 6-digit code'); return; }
    setLoading(true);
    try {
      const { data } = await authAPI.verify2fa({ code: tfaCode }, tempToken);
      localStorage.setItem('token', data.token);
      setAuth(data.user, data.token);
      toast.success('Welcome back!');
      navigate(data.user?.role === 'admin' ? '/admin' : '/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || '2FA verification failed');
    } finally { setLoading(false); }
  };

  // const handleGoogleLogin = () => {
  //   const redirectUri = `${window.location.origin}/login`;
  //   const scope = 'email profile';
  //   const url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${GOOGLE_CLIENT_ID}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=token&scope=${encodeURIComponent(scope)}&prompt=select_account`;
  //   window.location.href = url;
  // };

  const handleDemoLogin = async () => {
    setDemoLoading(true);
    try {
      const { data } = await authAPI.demoRegister();
      localStorage.setItem('token', data.token);
      setAuth(data.user, data.token);
      toast.success(`Demo account created! ID: ${data.user.user_id} — expires in 24 hours`);
      navigate('/dashboard');
      if (
  window.location.pathname.startsWith(
    '/mobile-auth'
  )
) {

  window.location.href =
    `metatradify://auth?token=${data.token}`;

  return;
}
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create demo account');
    } finally { setDemoLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center px-4" data-testid="login-page">
      <button onClick={toggleTheme} data-testid="login-theme-toggle" className="absolute top-4 right-4 p-2 rounded-lg bg-[var(--hover-bg)] border border-[var(--border-subtle)] transition-all z-10">
        {theme === 'dark' ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-green-500" />}
      </button>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 right-1/4 w-80 h-80 bg-green-500/5 rounded-full blur-[100px]" />
      </div>
      <div className="w-full max-w-[400px] relative">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <MetaTradifyLogo size={32} />
            <span className="text-lg font-bold font-['Outfit'] text-[var(--text-primary)]">metaTradify</span>
          </Link>
          <h1 className="text-2xl font-bold font-['Outfit'] tracking-tight text-[var(--text-primary)]">Welcome back</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">Register your trading account</p>
        </div>

        {!show2fa ? (
          <>
            {/* Demo Account Button */}
            <button
              onClick={handleDemoLogin}
              disabled={!captchaToken || demoLoading}
              data-testid="demo-account-btn"
              className="w-full h-11 mb-3 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 rounded text-sm font-medium text-amber-400 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Play className="w-4 h-4" />
              {demoLoading ? 'Creating demo...' : 'Try Demo Account — No signup needed, free $2000'}
            </button>

            {/* Google OAuth Button */}
            {/* <button
              onClick={handleGoogleLogin}
              disabled={googleLoading}
              data-testid="google-login-btn"
              className="w-full h-11 mb-4 bg-[var(--bg-surface)] hover:bg-[var(--hover-bg)] border border-[var(--border-subtle)] rounded text-sm font-medium text-[var(--text-primary)] transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-50"
            >
              <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 01-1.8 2.72v2.26h2.92A8.78 8.78 0 0017.64 9.2z"/><path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.71H.96v2.33A8.99 8.99 0 009 18z"/><path fill="#FBBC05" d="M3.97 10.71A5.41 5.41 0 013.68 9c0-.59.1-1.17.29-1.71V4.96H.96A8.99 8.99 0 000 9c0 1.45.35 2.82.96 4.04l3.01-2.33z"/><path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A8.99 8.99 0 00.96 4.96l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"/></svg>
              {googleLoading ? 'Connecting...' : 'Continue with Google'}
            </button> */}
{/* <button
   onClick={() => googleLogin()}
  disabled={!captchaToken || googleLoading}
  data-testid="google-login-btn"
  className="w-full h-11 mb-4 bg-[var(--bg-surface)] hover:bg-[var(--hover-bg)] border border-[var(--border-subtle)] rounded text-sm font-medium text-[var(--text-primary)] transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-50"
>
  <svg width="18" height="18" viewBox="0 0 18 18">
    <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 01-1.8 2.72v2.26h2.92A8.78 8.78 0 0017.64 9.2z"/>
    <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.71H.96v2.33A8.99 8.99 0 009 18z"/>
    <path fill="#FBBC05" d="M3.97 10.71A5.41 5.41 0 013.68 9c0-.59.1-1.17.29-1.71V4.96H.96A8.99 8.99 0 000 9c0 1.45.35 2.82.96 4.04l3.01-2.33z"/>
    <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A8.99 8.99 0 00.96 4.96l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"/>
  </svg>

  {googleLoading ? "Connecting..." : "Continue with Google for registration!"}
</button> */}
 <div ref={turnstileRef} className="w-full"></div>
            
          </>
        ) : (
          /* 2FA Verification */
          <form onSubmit={handle2faSubmit} className="space-y-4" data-testid="2fa-form">
            <div className="text-center mb-2">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-green-500/15 flex items-center justify-center">
                <ShieldCheck className="w-7 h-7 text-green-400" />
              </div>
              <p className="text-sm text-[var(--text-secondary)]">Enter the 6-digit code from your authenticator app</p>
            </div>
            <Input
              type="text" maxLength={6} placeholder="000000" value={tfaCode}
              onChange={(e) => setTfaCode(e.target.value.replace(/\D/g, ''))}
              data-testid="2fa-code-input"
              className="bg-[var(--bg-surface)] border-[var(--border-subtle)] text-[var(--text-primary)] text-center text-2xl font-mono tracking-[0.5em] h-14 focus:border-green-500"
            />
            <button type="submit" disabled={loading} data-testid="2fa-submit-btn"
              className="w-full h-11 bg-green-500 hover:bg-green-600 text-white rounded text-sm font-medium transition-all disabled:opacity-50">
              {loading ? 'Verifying...' : 'Verify'}
            </button>
            <button type="button" onClick={() => { setShow2fa(false); setTfaCode(''); }} className="w-full text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
              Back to Login
            </button>
          </form>
        )}

        

        
      </div>
    </div>
  );
}
